import {
  CdkConnectedOverlay,
  CdkOverlayOrigin,
  ConnectedOverlayPositionChange,
  ConnectedPosition,
  OverlayModule,
} from '@angular/cdk/overlay';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, DOCUMENT, Inject, Input, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngxs/store';
import { BehaviorSubject, fromEvent, Observable, Subscription } from 'rxjs';
import { Feature } from '@nimbus-global/features/feature-flipping/feature.enum';
import { FeatureFlippingService } from '@nimbus-global/features/feature-flipping/feature-flipping.service';
import { CompanionSplitScreenService } from '@nimbus-library/features/companion-chat/services/companion-split-screen.service';
import { LocalizationModule } from '@nimbus-library/features/localization/localization.module';
import { TranslationKeys } from '@nimbus-library/features/localization/models/enums/translation-keys.enum';
import { MenuEntryType } from '@nimbus-library/features/menu/models/menu-entry.model';
import { MenuEntryViewModel } from '@nimbus-library/features/menu/models/menu-entry.view-model';
import { ICONS } from '@nimbus-library/features/shared/resources/icons';
import { SharedUIModule } from '@nimbus-library/features/shared/shared.ui.module';
import { MenuActions } from '../../state/menu.actions';

@UntilDestroy()
@Component({
  standalone: true,
  selector: 'nimbus-menu-overflow-item',
  templateUrl: './menu-overflow-item.component.html',
  styleUrls: ['./menu-overflow-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, OverlayModule, LocalizationModule, SharedUIModule],
})
export class MenuOverflowItemComponent implements OnInit {
  @Input() public entries: MenuEntryViewModel[] = [];
  @Input() public active = false;
  @Input() public hasPermissionNotification = false;

  @ViewChild(CdkConnectedOverlay, { static: true }) public connectedOverlay: CdkConnectedOverlay;
  @ViewChild(CdkOverlayOrigin, { static: true }) public origin: CdkOverlayOrigin;

  private mouseMoveSubscription: Subscription;
  private openedByKeyboard = false;

  public isSplitScreenEnabled$: Observable<boolean>;
  public readonly isOpen$ = new BehaviorSubject<boolean>(false);
  public readonly opensUpwards$ = new BehaviorSubject<boolean>(false);
  public readonly TranslationKeys = TranslationKeys;
  public readonly Icons = ICONS;
  public readonly MenuEntryType = MenuEntryType;
  public readonly connectedPositions: ConnectedPosition[] = [
    { originX: 'end', originY: 'center', overlayX: 'start', overlayY: 'top', offsetX: 0, offsetY: -15 },
    { originX: 'end', originY: 'center', overlayX: 'start', overlayY: 'bottom', offsetX: 0, offsetY: 15 },
  ];

  public get hasUserPreferencesNotification(): boolean {
    return this.hasPermissionNotification && this.entries.some((entry) => entry.renderAs === MenuEntryType.UserPreferences);
  }

  private get originElement(): HTMLElement {
    return this.origin.elementRef.nativeElement as HTMLElement;
  }

  public constructor(
    private readonly store: Store,
    private readonly router: Router,
    private readonly featureFlippingService: FeatureFlippingService,
    private readonly companionSplitScreenService: CompanionSplitScreenService,
    @Inject(DOCUMENT) private readonly document: Document,
  ) {}

  public ngOnInit(): void {
    this.isSplitScreenEnabled$ = this.featureFlippingService.isEnabledInFeatureManagement(Feature.CompanionConversationSplitScreen);
  }

  public open(): void {
    this.isOpen$.next(true);
  }

  public close(): void {
    this.isOpen$.next(false);
  }

  public toggleByKeyboard(event: Event): void {
    event.preventDefault();
    this.openedByKeyboard = !this.isOpen$.value;
    this.isOpen$.next(this.openedByKeyboard);
  }

  public onOverlayKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.close();
      this.originElement.focus();
    }
  }

  public onPositionChange({ connectionPair }: ConnectedOverlayPositionChange): void {
    this.opensUpwards$.next(connectionPair.overlayY === 'bottom');
  }

  public navigate(entry: MenuEntryViewModel): void {
    this.close();

    if (entry.renderAs === MenuEntryType.UserPreferences) {
      this.router.navigate([entry.path], { queryParamsHandling: 'preserve' });
    } else {
      this.store.dispatch(new MenuActions.Navigate(entry));
    }
  }

  public navigateCompanionSplitScreen(): void {
    this.close();
    this.companionSplitScreenService.activate({ conversationId: null, messages: [] });
    this.router.navigate(['/']);
  }

  public overlayAttached(): void {
    const overlayElement = this.connectedOverlay.overlayRef.overlayElement;

    if (this.openedByKeyboard) {
      setTimeout(() => overlayElement.querySelector<HTMLElement>('[role="menuitem"]')?.focus());
    }

    // Same hover behaviour as the regular menu items: stay open while the pointer is on the item or the panel.
    this.mouseMoveSubscription = fromEvent<MouseEvent>(this.document, 'mousemove')
      .pipe(untilDestroyed(this))
      .subscribe((event) => {
        if (!this.contains(this.originElement, event) && !this.contains(overlayElement, event)) {
          this.close();
        }
      });
  }

  public overlayDetached(): void {
    this.mouseMoveSubscription?.unsubscribe();
    this.openedByKeyboard = false;
    this.close();
  }

  private contains(element: HTMLElement, { clientX, clientY }: MouseEvent): boolean {
    const rect = element.getBoundingClientRect();
    return clientX >= rect.left && clientX <= rect.right && clientY >= rect.top && clientY <= rect.bottom;
  }
}
